import isEqual from 'lodash/isEqual';
import noop from 'lodash/noop';
import throttle from 'lodash/throttle';
import * as React from 'react';

import { TinySliderSettings, TinySliderInstance, TinySliderInfo } from '../../../../typings/tiny-slider';
import { areChildrenEqual, getCarouselPadding } from '../../../utils/carousel';

/* генерируем настройки edgePadding под каждые 20px с 1300px до 1920px */
export const responsiveSettings = [...Array(191).keys()].reduce(
  (acc, key) => ({
    ...acc,
    [1300 + key * 20]: { edgePadding: getCarouselPadding(1300 + key * 20) },
  }),
  {},
);

interface ITinySliderState {
  defaultSettings: TinySliderSettings;
}

interface ITinySliderProps extends TinySliderSettings {
  startIndex: number;
  settings: TinySliderSettings;
  children: React.ReactElement[];
  addResponsive?: boolean;
  isTrackpadScrollEnabled?: boolean;
  onClick(slideClicked: Element | null, info: TinySliderInfo | null, event: React.MouseEvent): void;
  onIndexChanged(info: TinySliderInfo): void;
  onTransitionStart(info: TinySliderInfo): void;
  onTransitionEnd(info: TinySliderInfo): void;
  onTouchStart(info: TinySliderInfo): void;
  onTouchMove(info: TinySliderInfo): void;
  onTouchEnd(info: TinySliderInfo): void;
  onInit(initialized?: boolean): void;
}

export class TinySlider extends React.Component<ITinySliderProps, ITinySliderState> {
  private previousWidth = 0;
  public container = React.createRef<HTMLDivElement>();
  private slider: TinySliderInstance | null = null;
  private count = 0;
  private mergedSettings: TinySliderSettings = {};
  private dragging = false;
  private tns: ((options: TinySliderSettings) => TinySliderInstance) | null = null;
  private trackPadTimeout: number;
  public static defaultProps = {
    startIndex: 0,
    isTrackpadScrollEnabled: true,
    onInit: noop,
    onIndexChanged: noop,
    onTransitionStart: noop,
    onTransitionEnd: noop,
    onTouchStart: noop,
    onTouchMove: noop,
    onTouchEnd: noop,
  };

  public state = {
    defaultSettings: {},
  };

  private onClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const { onClick } = this.props;
    if (this.dragging || !onClick) {
      return;
    }

    if (!this.slider) {
      return onClick(null, null, event);
    }

    const info = this.slider.getInfo();
    const slideClicked = info.slideItems[info.index];

    onClick(slideClicked, info, event);
  };

  /** * Initialize the carousel plugin with new settings */
  private build(settings: TinySliderSettings) {
    if (!this.tns || this.slider) {
      return;
    }
    /* INIT CAROUSEL */
    this.mergedSettings = {
      ...this.state.defaultSettings,
      ...settings,
      responsive: this.props.addResponsive ? responsiveSettings : false,
      container: this.container.current || undefined,
      onInit: this.postInit,
    };

    this.slider = this.tns(this.mergedSettings);

    // if the slider doesn't load because there is just a child
    // so add class to normal behavior
    if (this.container.current) {
      this.container.current.className += ' tns-item';

      if (this.mergedSettings.lazyload) {
        Array.from(this.container.current.querySelectorAll('img')).forEach((img: HTMLImageElement) => {
          if (img.dataset.src) {
            img.src = img.dataset.src;
            img.className += ' loaded';
          }
        });
      }
    }
  }

  /**
   * Once the slider plugins has been initialized
   */
  private postInit = () => {
    if (!this.slider) {
      if (this.count >= 4) {
        this.props.onInit(false); // call initMetod anyway

        return;
      }
      this.count++;
      window.setTimeout(this.postInit, 100);

      return;
    }
    this.count = 0;

    const { events, goTo } = this.slider;
    const {
      onIndexChanged,
      onTouchStart,
      onTouchMove,
      onTouchEnd,
      startIndex,

      onInit,
    } = this.props;

    events.on('transitionStart', this.onTransitionStart);

    events.on('transitionEnd', this.onTransitionEnd);

    events.on('indexChanged', onIndexChanged);
    events.on('touchStart', onTouchStart);
    events.on('touchMove', onTouchMove);
    events.on('touchEnd', onTouchEnd);

    if (startIndex) {
      goTo(startIndex);
    }

    onInit(true);
  };

  // change draggin value to purge onClick event.
  private onTransitionStart = (info: TinySliderInfo) => {
    this.dragging = true;
    this.props.onTransitionStart(info);
  };

  private onTransitionEnd = (info: TinySliderInfo) => {
    this.dragging = false;
    this.props.onTransitionEnd(info);
  };

  public async componentDidMount() {
    await import('tiny-slider/dist/tiny-slider.css');
    const { tns } = await import('tiny-slider/src/tiny-slider');
    this.tns = tns;
    this.build(this.props.settings);
    window.addEventListener('resize', this.onResize);
    if (this.props.isTrackpadScrollEnabled && this.container.current) {
      this.container.current.addEventListener('wheel', this.onWheel);
    }
  }

  private onWheel = (e: WheelEvent) => {
    const { deltaX, deltaY } = e;
    if (Math.abs(deltaX) > Math.abs(deltaY)) {
      e.preventDefault();
      window.clearTimeout(this.trackPadTimeout);
      this.dispatchMouseEvent('mousedown');
      const scrollX = -deltaX;
      this.dispatchMouseEvent('mousemove', scrollX);
      this.trackPadTimeout = window.setTimeout(() => this.dispatchMouseEvent('mouseup', scrollX), 150);
    }
  };

  public UNSAFE_componentWillReceiveProps(nextProps: ITinySliderProps) {
    if (
      this.slider &&
      this.slider.destroy &&
      (!isEqual(nextProps.settings, this.props.settings) || !areChildrenEqual(nextProps.children, this.props.children))
    ) {
      this.slider.destroy();
      this.slider = null;
    }
  }

  public componentDidUpdate() {
    if (!this.slider) {
      this.build(this.props.settings);
    }
  }

  public componentWillUnmount() {
    window.removeEventListener('resize', this.onResize);
    if (this.slider && this.slider.destroy) {
      this.slider.destroy();
    }
  }

  public render() {
    return (
      <div ref={this.container} onClick={this.onClick}>
        {this.props.children}
      </div>
    );
  }

  private updateWidth = () => {
    const newWidth = window.innerWidth;
    if (this.slider && this.slider.refresh && this.previousWidth !== newWidth) {
      this.slider.refresh();
      this.previousWidth = newWidth;
    }
  };

  private onResize = throttle(this.updateWidth, 100);

  /* метод для создания событий мыши для текущего контейнера */
  private dispatchMouseEvent = (name: string, clientX: number = 0) => {
    if (this.container.current) {
      const mouseEvent = document.createEvent('MouseEvent');
      mouseEvent.initMouseEvent(name, true, true, window, 0, 0, 0, clientX, 0, false, false, false, false, 0, null);
      this.container.current.dispatchEvent(mouseEvent);
    }
  };
}
